import { Chapter } from "@/components/Chapter";
import { Reveal } from "@/components/Reveal";
import { CRAFT_IMAGE } from "@/data/content";

const REFINEMENTS = [
    "Circulation that feels natural",
    "Materials that age gracefully",
    "Forms that serve purpose, not just appearance",
];

const BELIEFS = [
    {
        title: "Craft Over Convention",
        text: "In a world of modular repetition, we choose craftsmanship. We do not assemble spaces from factory-made units forced into predefined systems—we create handcrafted environments, where every detail is intentional and every element belongs.",
    },
    {
        title: "Originality Over Imitation",
        text: "We do not replicate designs from the internet. Every NoirNest project is conceived from first principles—shaped by your persona, not borrowed from someone else’s.",
    },
];

const RefinementList = () => (
    <ul className="divide-y divide-wood/10 border-y border-wood/10">
        {REFINEMENTS.map((r, i) => (
            <li key={r} className="flex items-baseline gap-4 py-4">
                <span className="font-mono text-xs text-terra">0{i + 1}</span>
                <span className="text-base text-ink">{r}</span>
            </li>
        ))}
    </ul>
);

const CraftImage = () => (
    <Reveal className="relative">
        <div className="absolute -inset-0 -translate-x-3 translate-y-3 border border-gold/40" />
        <div className="group relative overflow-hidden">
            <img
                src={CRAFT_IMAGE.src}
                alt={CRAFT_IMAGE.alt}
                loading="lazy"
                className="aspect-[4/5] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
            />
        </div>
        <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.25em] text-fog">{CRAFT_IMAGE.caption}</p>
    </Reveal>
);

const AestheticsChapter = () => (
    <Chapter number="03" label="Aesthetics, Refined by Reality" title="Elegance and Function, in Quiet Harmony" testid="manifesto-chapter-04">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-14">
            <Reveal delay={0.15} className="space-y-6 text-base font-light leading-relaxed text-mute sm:text-lg">
                <p>
                    Aesthetic is not decoration—it is a reflection of how you see and experience the
                    world. We begin with that expression, then refine it through practicality. We
                    observe how you move, how you gather, how you rest—and shape spaces that respond
                    to real living.
                </p>
                <RefinementList />
                <p>
                    What emerges is not just a beautiful space—but a balanced environment, where
                    elegance and function exist in quiet harmony.
                </p>
            </Reveal>
            <div>
                <CraftImage />
            </div>
        </div>
        <Reveal delay={0.2}>
            <p className="mt-14 max-w-3xl font-serif text-2xl italic leading-snug text-wood sm:text-3xl">
                “True design is not what impresses once—it is what continues to feel right, every day.”
            </p>
        </Reveal>
    </Chapter>
);

const CoDesignChapter = () => (
    <Chapter number="04" label="Co-Design" title="A Shared Creative Journey" testid="manifesto-chapter-05" className="bg-sand">
        <Reveal delay={0.15} className="max-w-2xl space-y-6 text-base font-light leading-relaxed text-mute sm:text-lg">
            <p>
                We don’t believe in imposing design. We believe in conceiving it together. You bring
                your ideas, your inspirations, your instincts. We bring structure, clarity, and
                experience—refining those ideas into something cohesive and buildable.
            </p>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2">
            {BELIEFS.map((b, i) => (
                <Reveal key={b.title} delay={0.1 * i + 0.2}>
                    <div className="group h-full border border-wood/10 bg-ivory p-8 transition-colors duration-500 hover:border-terra/40 lg:p-10">
                        <span className="font-mono text-xs text-terra">{`0${i + 1}`}</span>
                        <h3 className="mt-4 font-serif text-xl font-medium text-ink sm:text-2xl">{b.title}</h3>
                        <p className="mt-4 text-sm font-light leading-relaxed text-mute">{b.text}</p>
                    </div>
                </Reveal>
            ))}
        </div>
    </Chapter>
);

export const Craft = () => (
    <>
        <AestheticsChapter />
        <CoDesignChapter />
    </>
);
